'use client';

import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';
import { cn } from '@/lib/utils';
import { Link } from '@/shared-ui/atoms';
import { MenuItem } from '@/shared-ui/molecules/menu-item';
import { ChevronRightIcon } from '@/shared-ui/icons';
import { BurgerMenuSubmenuHeader } from '../submenu-header';
/** @sfdc-extension-line SNK */
import { getCategoryHref } from '@/extensions/snk/lib/utils';
/** @sfdc-extension-line SNK */
import {
    BRANDS_DIRECTORY_PATH,
    getBrandLetterCategories,
    getBrandSectionId,
    isBrandGridLetterEnabled,
    type BrandsCuratedVariant,
} from '@/extensions/snk/lib/brands-utils';

type BurgerMenuBrandsProps = {
    brandsCategory: ShopperProducts.schemas['Category'];
    onBack: () => void;
    onCloseMenu: () => void;
    onOpenCurated: (variant: BrandsCuratedVariant) => void;
    onScrollToLetter: (sectionId: string) => void;
};

const CURATED_ITEMS: { variant: BrandsCuratedVariant; labelKey: string; defaultLabel: string }[] = [
    { variant: 'affordable', labelKey: 'burgerMenu.affordableBrands', defaultLabel: 'Affordable Brands' },
    { variant: 'bestselling', labelKey: 'burgerMenu.bestsellingBrands', defaultLabel: 'Bestselling Brands' },
    { variant: 'new', labelKey: 'burgerMenu.newToSpaceNk', defaultLabel: 'New to Space NK' },
];

export const BurgerMenuBrands = ({
    brandsCategory,
    onBack,
    onCloseMenu,
    onOpenCurated,
    onScrollToLetter,
}: BurgerMenuBrandsProps): ReactElement => {
    const { t } = useTranslation('header');

    const letterCategories = useMemo(() => getBrandLetterCategories(brandsCategory), [brandsCategory]);

    return (
        <div className="flex flex-col">
            <BurgerMenuSubmenuHeader title={t('burgerMenu.brands', 'Brands')} onBack={onBack} />

            <ul className="list-none">
                <li>
                    <MenuItem
                        textAlign="left"
                        textWeight="medium"
                        showBottomBorder
                        className="rounded-none uppercase font-bold"
                        textVariant="h4"
                        asChild>
                        <Link to={BRANDS_DIRECTORY_PATH} onClick={onCloseMenu}>
                            {t('burgerMenu.brandDirectory', 'Brand Directory')}
                        </Link>
                    </MenuItem>
                </li>

                {CURATED_ITEMS.map(({ variant, labelKey, defaultLabel }) => (
                    <li key={variant}>
                        <MenuItem
                            textAlign="left"
                            textVariant="h4"
                            showBottomBorder
                            rightIcon={ChevronRightIcon}
                            className="rounded-none uppercase"
                            onClick={() => onOpenCurated(variant)}>
                            {t(labelKey, defaultLabel)}
                        </MenuItem>
                    </li>
                ))}
            </ul>

            <div className="grid grid-cols-7 gap-2 px-4 py-6">
                {letterCategories.map((letterCategory) => {
                    const enabled = isBrandGridLetterEnabled(letterCategory);
                    const label = letterCategory.name ?? letterCategory.id;

                    return (
                        <button
                            key={letterCategory.id}
                            type="button"
                            disabled={!enabled}
                            onClick={() => onScrollToLetter(getBrandSectionId(letterCategory.id))}
                            className={cn(
                                'flex h-10 items-center justify-center font-normal uppercase text-h3',
                                enabled
                                    ? 'cursor-pointer bg-transparent rounded text-foreground hover:bg-accent'
                                    : 'cursor-not-allowed text-muted-foreground'
                            )}
                            aria-label={t('burgerMenu.jumpToBrands', {
                                letter: label,
                                defaultValue: 'Jump to brands starting with {{letter}}',
                            })}>
                            {label}
                        </button>
                    );
                })}
            </div>

            {letterCategories.map((letterCategory) => {
                if (!isBrandGridLetterEnabled(letterCategory)) {
                    return null;
                }

                const brands = letterCategory.categories ?? [];

                if (brands.length === 0) {
                    return null;
                }

                return (
                    <section key={letterCategory.id} id={getBrandSectionId(letterCategory.id)} className="scroll-mt-4">
                        <h2 className="px-4 py-4 text-4xl font-normal uppercase tracking-wide text-foreground">
                            {letterCategory.name}
                        </h2>
                        <ul className="list-none">
                            {brands.map((brand) => (
                                <li key={brand.id}>
                                    <MenuItem
                                        textAlign="left"
                                        textVariant="p"
                                        textWeight="light"
                                        className="rounded-none"
                                        asChild>
                                        <Link to={getCategoryHref(brand.id)} onClick={onCloseMenu}>
                                            {brand.name}
                                        </Link>
                                    </MenuItem>
                                </li>
                            ))}
                        </ul>
                    </section>
                );
            })}
        </div>
    );
};
